import { Box, Tooltip, useTheme } from '@mui/material';
import { AutoIsoObjectType } from '../types/AutoIsoModel';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import { useMemo, forwardRef } from 'react';
import { getTeamColor } from '@/features/teams/functions/getTeamColor';

function roundUpToNDecimalPlaces(num: number, decimalPlaces: number): number {
  const multiplier = 10 ** decimalPlaces;
  return Math.ceil(num * multiplier) / multiplier;
}

function getContrastColor(hex: string): string {
  // Remove the '#' if it's included
  hex = hex.replace('#', '');

  // Parse the hex color to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate the luminance of the color
  const luminance = 0.2126 * (r / 255) + 0.7152 * (g / 255) + 0.0722 * (b / 255);

  // Return white or black based on luminance threshold
  return luminance > 0.5 ? 'black' : 'white';
}

export function getEntityObjectSize(entityType: string): number {
  return entityType === AutoIsoObjectType.Player || entityType === AutoIsoObjectType.Official
    ? 28
    : 15;
}

export function calculateEntityPosition(
  pos: { x: number; y: number },
  elementWidth: number,
  elementHeight: number,
  surfaceWidth: number,
  surfaceHeight: number,
  objectSize: number,
): { left: number; bottom: number } {
  const xPercent = (pos.x / (surfaceWidth / 2) + 1) / 2.0;
  const yPercent = (pos.y / (surfaceHeight / 2) + 1) / 2.0;
  // Clamp inside the court so an out-of-bounds tracking position (xPercent
  // outside [0,1]) pins the entity to the edge instead of overflowing the
  // container — which otherwise triggers a scrollbar that shifts the layout.
  const maxLeft = Math.max(0, elementWidth - objectSize);
  const maxBottom = Math.max(0, elementHeight - objectSize);
  const xVal = Math.min(maxLeft, Math.max(0, (elementWidth - objectSize) * xPercent));
  const yVal = Math.min(maxBottom, Math.max(0, (elementHeight - objectSize) * yPercent));
  return {
    left: roundUpToNDecimalPlaces(xVal, 0),
    bottom: roundUpToNDecimalPlaces(yVal, 0),
  };
}

type Props = {
  awayTeam: any;
  entity: any;
  objectOfPlay: string;
  homeTeam: any;
  setTop: any;
  top: any;
  game: any;
};

export const TrackedEntity = forwardRef<HTMLDivElement, Props>(
  ({ awayTeam, entity, objectOfPlay, homeTeam, setTop, top, game }, ref) => {
    const { singleCurrentlyTrackedObjectId, trackEntity } = useServerStateContext();
    const isTracked = entity.id.toString() === singleCurrentlyTrackedObjectId.toString();
    const theme = useTheme();
    const label = useMemo(
      () =>
        entity.type === AutoIsoObjectType.Player || entity.type === AutoIsoObjectType.Official
          ? entity.playerNumber
          : objectOfPlay?.charAt(0).toUpperCase(),
      [objectOfPlay, entity.playerNumber, entity.type],
    );

    const objectSize = useMemo(() => getEntityObjectSize(entity.type), [entity.type]);

    const backgroundColor = useMemo(() => {
      // If this is the currently tracked object, return white
      if (entity.id === singleCurrentlyTrackedObjectId) {
        return '#ffffff';
      }

      // Handle players
      if (entity.type === AutoIsoObjectType.Player) {
        const isHomeTeam = entity.playerTeamId === homeTeam.id;
        const team = isHomeTeam ? homeTeam : awayTeam;

        const fallbackColor = isHomeTeam ? theme.palette.info.main : theme.palette.success.main;

        return getTeamColor(team, game, fallbackColor);
      }

      // Handle officials and other entities
      return entity.type === AutoIsoObjectType.Official
        ? theme.palette.grey[700]
        : theme.palette.primary.main;
    }, [entity, homeTeam, awayTeam, singleCurrentlyTrackedObjectId, theme.palette]);

    const color = useMemo(() => {
      return getContrastColor(backgroundColor);
    }, [backgroundColor]);

    const objectLabelFontSize = objectSize / 2.1;
    const zIndex =
      entity.id === 1 ? 10000 : isTracked || top === entity.id ? 1000 : entity.playerNumber;

    return (
      <Tooltip title={entity.displayName}>
        <Box
          ref={ref}
          sx={{
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'normal',
            cursor: 'pointer',
            width: objectSize,
            height: objectSize,
            borderRadius: objectSize / 2,
            backgroundColor,
            fontSize: objectLabelFontSize,
            color: color,
            border: `1px solid ${color}`,
            zIndex,
            opacity: isTracked || top === entity.id ? 1 : 0.7,
            ...(isTracked && {
              color : 'black',
              backgroundColor: '#ccff02',
              border: `1px solid black`,
            }),
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            trackEntity(entity, `Virtual surface`, 'Virtual Surface');
          }}
          onMouseOver={() => setTop(entity.id)}
        >
          {label}
        </Box>
      </Tooltip>
    );
  },
);
